import { zodResolver } from '@hookform/resolvers/zod';
import PrintRounded from '@mui/icons-material/PrintRounded';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { initials } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useSaveSettings, type ShopSettings } from '../api';
import { ChoiceCards, SaveBar, SectionCard, SettingSwitch } from '../ui';

type Paper = 'A4' | 'A5' | 'THERMAL_80MM';

const schema = z.object({
  paperSize: z.enum(['A4', 'A5', 'THERMAL_80MM']),
  showLogo: z.boolean(),
  showTaxNumber: z.boolean(),
  footerText: z.string().max(500, 'At most 500 characters'),
  termsText: z.string().max(2000, 'At most 2000 characters'),
});
type Values = z.infer<typeof schema>;

function toValues(s: ShopSettings): Values {
  // Defaults first: older shops may have a partial preferences object.
  const p: Values = {
    paperSize: 'A4',
    showLogo: true,
    showTaxNumber: true,
    footerText: '',
    termsText: '',
    ...(s.printPreferences as Partial<Values>),
  };
  return {
    paperSize: p.paperSize,
    showLogo: p.showLogo,
    showTaxNumber: p.showTaxNumber,
    footerText: p.footerText,
    termsText: p.termsText,
  };
}

function PaperIcon({ kind }: { kind: Paper }) {
  const dims = { A4: [20, 28], A5: [16, 22], THERMAL_80MM: [11, 28] }[kind];
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Box
        sx={{
          width: dims[0],
          height: dims[1],
          borderRadius: '3px',
          border: '1.5px solid currentColor',
          backgroundImage:
            'repeating-linear-gradient(180deg, transparent 0 4px, currentColor 4px 5px)',
          backgroundSize: '60% 100%',
          backgroundPosition: 'center 5px',
          backgroundRepeat: 'no-repeat',
          opacity: 0.85,
        }}
      />
    </Box>
  );
}

export function PrintTab({ settings, canManage }: { settings: ShopSettings; canManage: boolean }) {
  const notify = useNotify();
  const save = useSaveSettings();
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<Values>({ resolver: zodResolver(schema), values: toValues(settings) });
  const ro = !canManage;

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ printPreferences: v });
      notify.success('Print preferences saved');
    } catch (e) {
      notify.error(e);
    }
  });

  const v = watch();

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <SectionCard
        icon={<PrintRounded />}
        title="Printing"
        description="Defaults for printed invoices, receipts and statements."
      >
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1.5fr) minmax(0,1fr)' },
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                Paper size
              </Typography>
              <Controller
                control={control}
                name="paperSize"
                render={({ field }) => (
                  <ChoiceCards
                    label="Paper size"
                    columns={3}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={ro}
                    options={[
                      {
                        value: 'A4',
                        title: 'A4',
                        description: 'Standard office printer',
                        icon: <PaperIcon kind="A4" />,
                      },
                      {
                        value: 'A5',
                        title: 'A5',
                        description: 'Half-page invoices',
                        icon: <PaperIcon kind="A5" />,
                      },
                      {
                        value: 'THERMAL_80MM',
                        title: 'Thermal 80 mm',
                        description: 'Counter receipt printer',
                        icon: <PaperIcon kind="THERMAL_80MM" />,
                      },
                    ]}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name="showLogo"
                render={({ field }) => (
                  <SettingSwitch
                    title="Show logo"
                    description="Print your logo in the document header."
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={ro}
                  />
                )}
              />
              <Controller
                control={control}
                name="showTaxNumber"
                render={({ field }) => (
                  <SettingSwitch
                    title="Show tax number"
                    description="Print your tax registration number under the shop name."
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={ro}
                  />
                )}
              />
            </Box>
            <TextField
              label="Footer note"
              placeholder="Thank you for your business!"
              multiline
              minRows={2}
              disabled={ro}
              {...register('footerText')}
              error={Boolean(errors.footerText)}
              helperText={errors.footerText?.message ?? `${String(v.footerText.length)}/500`}
            />
            <TextField
              label="Terms & conditions"
              placeholder="Goods once sold can be exchanged within 7 days with the receipt."
              multiline
              minRows={3}
              disabled={ro}
              {...register('termsText')}
              error={Boolean(errors.termsText)}
              helperText={errors.termsText?.message ?? `${String(v.termsText.length)}/2000`}
            />
          </Stack>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Preview
            </Typography>
            <DocumentPreview settings={settings} values={v} />
          </Box>
        </Box>
      </SectionCard>
      <SaveBar
        dirty={isDirty}
        saving={save.isPending}
        disabled={ro}
        onReset={() => reset(toValues(settings))}
      />
    </form>
  );
}

function DocumentPreview({ settings, values }: { settings: ShopSettings; values: Values }) {
  const thermal = values.paperSize === 'THERMAL_80MM';
  const lines = [
    ['Cement 50kg', '10', '14,500'],
    ['PVC pipe ¾″', '24', '6,240'],
    ['Wall putty', '3', '4,350'],
  ];
  const ink = '#101828';
  const muted = '#667085';
  return (
    <Box
      sx={(t) => ({
        bgcolor: '#EEF0F4',
        backgroundImage: 'radial-gradient(rgba(16,24,40,0.06) 1px, transparent 1px)',
        backgroundSize: '14px 14px',
        borderRadius: '16px',
        p: 2.5,
        display: 'grid',
        placeItems: 'center',
        ...t.applyStyles('dark', {
          bgcolor: '#1D2939',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        }),
      })}
    >
      <Box
        sx={{
          width: thermal ? 170 : values.paperSize === 'A5' ? 220 : 260,
          aspectRatio: thermal ? 'auto' : values.paperSize === 'A5' ? '148 / 210' : '210 / 297',
          minHeight: thermal ? 300 : undefined,
          bgcolor: '#fff',
          color: ink,
          borderRadius: '4px',
          boxShadow: '0 12px 28px -10px rgba(16,24,40,0.3), 0 2px 6px rgba(16,24,40,0.08)',
          p: thermal ? 1.5 : 2,
          fontSize: 7.5,
          lineHeight: 1.45,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 220ms cubic-bezier(0.2,0,0,1)',
          textAlign: thermal ? 'center' : 'left',
        }}
      >
        <Stack
          direction={thermal ? 'column' : 'row'}
          spacing={1}
          sx={{ alignItems: thermal ? 'center' : 'flex-start' }}
        >
          {values.showLogo && (
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '6px',
                bgcolor: 'primary.main',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: 8,
                flexShrink: 0,
              }}
            >
              {initials(settings.name)}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontWeight: 800, fontSize: 9.5 }}>{settings.name}</Box>
            <Box sx={{ color: muted }}>{settings.address ?? 'Shop address'}</Box>
            {values.showTaxNumber && settings.taxNumber && (
              <Box sx={{ color: muted }}>Tax no. {settings.taxNumber}</Box>
            )}
          </Box>
          {!thermal && (
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ fontWeight: 800, fontSize: 9, color: 'primary.main' }}>INVOICE</Box>
              <Box sx={{ color: muted }}>INV-2026-00042</Box>
            </Box>
          )}
        </Stack>
        <Box sx={{ my: 1, borderTop: `1px ${thermal ? 'dashed' : 'solid'} #E4E7EC` }} />
        {lines.map(([n, q, a]) => (
          <Stack key={n} direction="row" sx={{ py: 0.25, textAlign: 'left' }}>
            <Box sx={{ flex: 1 }}>{n}</Box>
            <Box sx={{ width: 22, textAlign: 'right', color: muted }}>{q}</Box>
            <Box sx={{ width: 40, textAlign: 'right' }}>{a}</Box>
          </Stack>
        ))}
        <Box sx={{ my: 1, borderTop: `1px ${thermal ? 'dashed' : 'solid'} #E4E7EC` }} />
        <Stack direction="row" sx={{ fontWeight: 800, fontSize: 9 }}>
          <Box sx={{ flex: 1, textAlign: 'left' }}>Total</Box>
          <Box>Rs 25,090</Box>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 12 }} />
        {values.termsText && (
          <Box
            sx={{
              color: muted,
              mb: 0.75,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              textAlign: 'left',
            }}
          >
            <b>Terms:</b> {values.termsText}
          </Box>
        )}
        {values.footerText && (
          <Box sx={{ textAlign: 'center', fontWeight: 600, color: ink }}>{values.footerText}</Box>
        )}
      </Box>
    </Box>
  );
}
